import {ServiceBase} from "../ServiceBase";
import {IExitPopupService} from "./exit-popup.service.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {EXIT_POPUP_DEFAULT_CONFIG, EXIT_POPUP_LOCAL_STORAGE_KEY, numberOfCorrelatedGames} from "./exit-popup-consts";
import {Check} from "@sparkware/app-utils";
import {action, observable} from "mobx";
import {GameInfo} from "../games/game-info";
import {OnGameCloseParams} from "../sdk/sdk-events";


export interface IExitPopupLocalStorage {
    sessionChance: number;
    dontShowPopupAgain: boolean;
    numberOfAppearancesInSession: number;
}


export class ExitPopupService extends ServiceBase implements IExitPopupService {
    private _numberOfAppearancesInSession: number = 0;
    private _sessionChance: number = 0;
    @observable.ref public dontShowPopupAgain: boolean;


    constructor(services: IServiceFactoryExtended) {
        super(services);
        this._initDataFromLocalStorage();
        this._subscribe();
    }

    _subscribe() {
        this.services.sdk.events.onOAuthStateChanged(()=> {
            this._numberOfAppearancesInSession = 0;
            this._setExitPopupDataInLocalStorage();
        });

        this.services.sdk.events.onGameClose((properties: OnGameCloseParams)=> {
            if(properties.showNextGameSuggestion && properties.isGamePlayed) {
                return this._showExitPopup(properties.gameType, properties.isReal);
            }

            return Promise.resolve();
        });
    }

    _onOpenRealGame(gameInfo: GameInfo, closedGameType: number) {
        this._updateMessageChance(EXIT_POPUP_DEFAULT_CONFIG.PLAY_INCREASE_CHANCE);
        this._sendAnalytics(
            'click_play',
            `${this._sessionChance}_${closedGameType}_${gameInfo.gameType}_${EXIT_POPUP_DEFAULT_CONFIG.PLAY_INCREASE_CHANCE}`,
            this._numberOfAppearancesInSession
        );
        this._setExitPopupDataInLocalStorage();
    }

    _onCancel(recommendedGame: GameInfo, closedGameType: number): void {
        this._updateMessageChance(EXIT_POPUP_DEFAULT_CONFIG.CANCEL_DECREASE_CHANCE * -1);
        this._sendAnalytics(
            'click_not_now',
            `${this._sessionChance}_${closedGameType}_${recommendedGame.gameType}_${EXIT_POPUP_DEFAULT_CONFIG.CANCEL_DECREASE_CHANCE}`,
            this._numberOfAppearancesInSession
        );
        this._dontShowAgainGAEvent();
        this._setExitPopupDataInLocalStorage();
    }

    _dontShowAgainGAEvent() {
        if (!this.dontShowPopupAgain) {
            return;
        }

        this._sendAnalytics(
            'dont_show_again',
            `${this.dontShowPopupAgain}`,
            this._numberOfAppearancesInSession
        );
    }

    _sendAnalytics(action = '', label = '', value = 0): void {
        const analyticObject = {
            category: 'exitPopUpRecommendation',
            action: action,
            label: label,
            value: value
        };

        this.services.analytics.sendAnalytics(analyticObject.category, analyticObject.action, analyticObject.label, analyticObject.value);
    }

    /**
     * @description Hold the logic if to show or not to show the exit popup
     * @return {boolean}
     * @private
     */
    _isMessageCanBeShown(isReal: boolean): boolean {
        //If it is can be shown during session (will be false if the user clicked don't show again or _numberOfAppearancesInSession if larger then define)
        if (this.dontShowPopupAgain) {
            this.services.logger.info(`Exit game didn't show because dontShowPopupAgain is ${this.dontShowPopupAgain}`);
            return false;
        }

        //Don't show the pop up again if we show the popup more then the max appearance per session
        if (this._numberOfAppearancesInSession >= EXIT_POPUP_DEFAULT_CONFIG.MAX_APPEARANCES_PER_SESSION) {
            this.services.logger.info(`Exit game didn't show because _numberOfAppearancesInSession is ${this._numberOfAppearancesInSession}`);
            return false;
        }

        //Don't show the pop If that game wasn't played (the balance didn't change) unless we support also demo mode and then it will always pass
        if (!isReal) {
            this.services.logger.info(`Exit game will not show because support only real mode`);
            return false;
        }

        return this._calcRandomChance();
    }

    /**
     * @description Calculate random chance if to show or not to show
     * @private
     */
    _calcRandomChance() {
        const randomChance = Math.ceil(Math.random() * 100);
        this.services.logger.info('Random chance = ' + randomChance + ' Session Chance = ' + this._sessionChance);

        return randomChance <= this._sessionChance;
    }

    async _showExitPopup(gameType: number, isReal: boolean): Promise<boolean> {
        if (!this._isMessageCanBeShown(isReal)) {
            return Promise.resolve(false);
        }

        this._openExitPopupMessage(gameType);
        return Promise.resolve(true);
    }

    @action.bound
    _clickDontShowPopupAgain() {
        this.dontShowPopupAgain = !this.dontShowPopupAgain;
    }

    async _openExitPopupMessage(playedGameType: number): Promise<void> {
        const correlatedGame = await this._getCorrelatedGame(playedGameType);
        const gameInfo: GameInfo = await this.services.games.getGameInfo(correlatedGame);

        if(!gameInfo) {
            this.services.logger.error('There is no game info, exit popup game message can not be displayed! gameType: ' + correlatedGame);
            return Promise.resolve()
        }

        this.services.gamePopup.showSuggestedGame(
            correlatedGame,
            this.services.translation.translate("EXIT_POPUP_TITLE"),
            "",
            ()=> {this._onPopupClosed()},
            ()=> {this._onCloseButtonClicked(gameInfo, playedGameType)},
            ()=> {this._onCancel(gameInfo, playedGameType)},
            ()=> {this._onOpenRealGame(gameInfo, playedGameType)},
            {display: true, text: this.services.translation.translate('DONT_SHOW_AGAIN_TEXT'), onClickHandler: ()=> {this._clickDontShowPopupAgain()}},
            this.services.abTestingFeatureDataProvider.isExitPopupSquareIconsEnabled);

        this._numberOfAppearancesInSession++;
        this._sendAnalytics(
            'view',
            `${this._sessionChance}_${playedGameType}_${gameInfo.gameType}`,
            this._numberOfAppearancesInSession
        );
    }

    /**
     * @description Get the correlated games
     * @Note - We will got the top 6 games for this game and randomly chose one of them
     * @param gameType of the game that was just played
     * @return Correlated gameType
     * @private
     */
    _getCorrelatedGame(gameType: number): Promise<number>{
        return this.services.games.getCorrelatedGames(gameType, numberOfCorrelatedGames).then((correlatedGames: number[])=>{
            if (correlatedGames.length > 0) {
                const index = Math.floor(Math.random() * (correlatedGames.length));
                //Return randomly one of the 6 related games
                return correlatedGames[index];
            } else {
                this.services.logger.info(`Exit pop was not show because we didnt got correlated games for game type ${gameType}`)
                return Promise.reject(false);
            }
        });
    }

    @action.bound
    _initDataFromLocalStorage(): void {
        const exitPopUpData: IExitPopupLocalStorage = this._getExitPopupDataInLocalStorage();

        this.dontShowPopupAgain = (exitPopUpData && exitPopUpData.dontShowPopupAgain) || false;
        this._numberOfAppearancesInSession = (exitPopUpData && exitPopUpData.numberOfAppearancesInSession) || 0;

        //Init the session with default chance or previews chance
        const chance = Check.isNullOrUndefined(exitPopUpData &&exitPopUpData.sessionChance) ? EXIT_POPUP_DEFAULT_CONFIG.INITIAL_CHANCE : exitPopUpData.sessionChance;


        this._updateMessageChance(chance);
    }

    _updateMessageChance(chance): void {
        const newChance = this._sessionChance + chance;
        this._sessionChance = newChance > EXIT_POPUP_DEFAULT_CONFIG.MAX_CHANCE ? EXIT_POPUP_DEFAULT_CONFIG.MAX_CHANCE : newChance;
        this.services.logger.info('_updateMessageChance, New chance = ' + this._sessionChance);
        //Update the chance in the LocalStorage
        this._setExitPopupDataInLocalStorage();
    }

    _setExitPopupDataInLocalStorage() {
        const exitPopupData: IExitPopupLocalStorage = {
            sessionChance: this._sessionChance || 0,
            dontShowPopupAgain: this.dontShowPopupAgain || false,
            numberOfAppearancesInSession: this._numberOfAppearancesInSession || 0
        };

        this.services.storage.setItem(EXIT_POPUP_LOCAL_STORAGE_KEY, JSON.stringify(exitPopupData));
    }


    _onPopupClosed(): void {
        this._setExitPopupDataInLocalStorage();
        this._dontShowAgainGAEvent();
    }

    _onCloseButtonClicked(recommendedGame: GameInfo, closedGameType: number): void {
        this._sendAnalytics(
            'click_close_button',
            `${this._sessionChance}_${closedGameType}_${recommendedGame.gameType}`,
            this._numberOfAppearancesInSession
        );
    }

    _getExitPopupDataInLocalStorage() {
        return JSON.parse(this.services.storage.getItem(EXIT_POPUP_LOCAL_STORAGE_KEY));
    }
}